import { rewriteAzureBlobUrlToContentDomain } from './azureBlobUrl'

function sasFetchHeaders(sasUrl: string): Record<string, string> {
  const headers: Record<string, string> = {}
  if (sasUrl.includes('.ngrok-free.app')) {
    headers['ngrok-skip-browser-warning'] = 'true'
  }
  return headers
}

export function normalizeFileExt(ext: string | undefined): string {
  if (!ext?.trim()) return ''
  const t = ext.trim()
  return t.startsWith('.') ? t : `.${t}`
}

/** PDF/images open in a new tab; everything else downloads. */
export function isOperatorFileViewableInTab(ext: string | undefined): boolean {
  const e = normalizeFileExt(ext).replace(/^\./, '').toLowerCase()
  return ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(e)
}

export function mimeTypeForExtension(ext: string): string {
  const e = ext.replace(/^\./, '').toLowerCase()
  switch (e) {
    case 'pdf':
      return 'application/pdf'
    case 'png':
      return 'image/png'
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    case 'gif':
      return 'image/gif'
    case 'webp':
      return 'image/webp'
    case 'svg':
      return 'image/svg+xml'
    default:
      return 'application/octet-stream'
  }
}

function buildFileName(
  opts: { fileId: string; fileName?: string; displayName?: string },
  ext: string,
): string {
  if (opts.fileName?.trim()) return opts.fileName.trim()
  const name = opts.displayName?.trim()
  if (name) {
    if (ext && !name.toLowerCase().endsWith(ext.toLowerCase())) return `${name}${ext}`
    return name
  }
  return `${opts.fileId}${ext}`
}

function triggerDownload(url: string, fileName: string): void {
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

export type OpenOperatorFileOptions = {
  /** From operator payload (`fileExt` / `FileExt`) — drives view vs download. */
  fileExt?: string
  /** Fallback extension for download naming when payload row has none. */
  catalogExt?: string
  /** Payload `fileName` when present; otherwise title + `fileExt`. */
  fileName?: string
  displayName?: string
  /**
   * Blank tab opened synchronously on click (before any await) for PDF/images.
   * Navigated to a blob URL after fetch so pop-up blockers do not fire.
   */
  viewTab?: Window | null
}

/**
 * Present a cached or fetched blob (PDF/images in tab, other types as download).
 */
export async function presentOperatorFileBlob(
  blob: Blob,
  opts: { fileId: string } & OpenOperatorFileOptions,
): Promise<void> {
  const payloadExt = normalizeFileExt(opts.fileExt)
  const namingExt = payloadExt || normalizeFileExt(opts.catalogExt)
  const viewInTab = isOperatorFileViewableInTab(payloadExt)
  const mime = namingExt
    ? mimeTypeForExtension(namingExt)
    : blob.type || 'application/octet-stream'
  const objectUrl = URL.createObjectURL(blob.type === mime ? blob : new Blob([blob], { type: mime }))

  if (viewInTab) {
    const tab = opts.viewTab
    if (!tab || tab.closed) {
      URL.revokeObjectURL(objectUrl)
      throw new Error('Could not open a new tab. Allow pop-ups for this site and try again.')
    }
    tab.location.href = objectUrl
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000)
    return
  }

  triggerDownload(objectUrl, buildFileName(opts, namingExt))
  URL.revokeObjectURL(objectUrl)
}

/**
 * Fetch SAS blob, then either navigate a pre-opened tab (PDF/images) or download (other types).
 * Never opens the raw SAS URL — Azure Content-Disposition can trigger a conflicting Save dialog.
 */
export async function presentOperatorFile(
  sasUrl: string,
  opts: { fileId: string } & OpenOperatorFileOptions,
): Promise<void> {
  const url = rewriteAzureBlobUrlToContentDomain(sasUrl)
  const res = await fetch(url, { method: 'GET', headers: sasFetchHeaders(url) })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`SAS fetch failed ${res.status}: ${text.slice(0, 200)}`)
  }
  const blob = await res.blob()
  const namingExt = normalizeFileExt(opts.fileExt) || normalizeFileExt(opts.catalogExt)
  const mime = namingExt
    ? mimeTypeForExtension(namingExt)
    : blob.type || 'application/octet-stream'
  await presentOperatorFileBlob(new Blob([blob], { type: mime }), opts)
}

export function isExpiredSasFetchError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error)
  return /\b403\b/.test(msg) || /\b401\b/.test(msg)
}

/** Fetch operator blob SAS into an object URL for inline `<img>` / embed display. */
export async function fetchOperatorFileBlobUrl(
  sasUrl: string,
  ext: string | undefined,
): Promise<string> {
  const url = rewriteAzureBlobUrlToContentDomain(sasUrl)
  const res = await fetch(url, { method: 'GET', headers: sasFetchHeaders(url) })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`SAS fetch failed ${res.status}: ${text.slice(0, 200)}`)
  }
  const blob = await res.blob()
  const namingExt = normalizeFileExt(ext)
  const mime = namingExt
    ? mimeTypeForExtension(namingExt)
    : blob.type || 'application/octet-stream'
  return URL.createObjectURL(new Blob([blob], { type: mime }))
}
